import React, { useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { NoSsr } from '@withjoy/joykit';
import type { PreloadImagesProps } from './PreloadImages.types';
import { useSmartPreloadImageUrls } from './useSmartPreloadImageUrls';

/**
 * Injects `<link rel="preload" as="image">` into the HTML `<head>` during SSR
 * for the current route's critical images. This lets the browser start fetching
 * them before React hydration, improving LCP.
 */
const CriticalImagePreloads: React.FC<{ urls: ReadonlyArray<string> }> = ({ urls }) => {
  if (urls.length === 0) return null;
  return (
    <Helmet>
      {urls.map((url, index) => (
        <link
          key={url}
          rel="preload"
          as="image"
          href={url}
          // fetchpriority is not yet in @types/react (added in React 18.3)
          // but React 17 passes through unknown lowercase HTML attributes to the DOM.
          // The first critical URL is the LCP image and deserves the highest fetch priority.
          {...(index === 0 ? ({ fetchpriority: 'high' } as {}) : {})}
        />
      ))}
    </Helmet>
  );
};

/**
 * Preloads non-critical images in the background after the main thread is idle.
 * Uses `new Image()` instead of hidden DOM nodes for a cleaner approach.
 * A ref-based Set guards against redundant instantiation if the effect re-fires.
 */
const DeferredImagePreloads: React.FC<{ urls: ReadonlyArray<string> }> = ({ urls }) => {
  const loadedRef = useRef(new Set<string>());

  useEffect(() => {
    if (urls.length === 0) return;

    // eslint-disable-next-line compat/compat
    const schedule = window.requestIdleCallback || ((cb: () => void) => window.setTimeout(cb, 1));
    const cancel = window.cancelIdleCallback || ((id: number) => window.clearTimeout(id));

    const handle = schedule(() => {
      urls.forEach(url => {
        if (!loadedRef.current.has(url)) {
          loadedRef.current.add(url);
          const img = new Image();
          img.src = url;
        }
      });
    });

    return () => {
      cancel(handle as number);
    };
  }, [urls]);

  return null;
};

/**
 * Route-aware image preloader for guest site layouts.
 *
 * - Critical images (for the current route) get `<link rel="preload">` in the SSR HTML `<head>`
 * - Remaining images are deferred to `requestIdleCallback` after initial paint
 */
const PreloadImages: React.FC<PreloadImagesProps> = ({ pages, eventHandle, headerPhoto, fallbackPhotoUrl }) => {
  const { criticalUrls, deferredUrls } = useSmartPreloadImageUrls(pages, eventHandle, headerPhoto, fallbackPhotoUrl);

  return (
    <>
      <CriticalImagePreloads urls={criticalUrls} />
      <NoSsr>
        <DeferredImagePreloads urls={deferredUrls} />
      </NoSsr>
    </>
  );
};

export { PreloadImages };
